import { Badge, Spin, Tooltip } from "antd";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetStaffAvailabilities } from "~/services/staff/staff-availability.queries";
import { useGetAllStaff } from "~/services/operation";
import { StaffAvailabilitySchedule } from "~/types/staff-availability-schedule";
import { cn } from "~/utils";
import { formatStaffName } from "~/utils/format-staff-name";
import { StaffAvailability } from "~/utils/constants";
import { StaffDetailsSidebar } from "./staff-details-sidebar";

// Extend Day.js with plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface AvailabilityCalendarProps {
  staffId?: string; // Optional staffId prop
}

interface StaffCounts {
  availableStaff: number; // FULL_TIME + PART_TIME staff
  availableDrivers: number; // FULL_TIME + PART_TIME drivers
  availableLeads: number; // FULL_TIME + PART_TIME leads
  staffBooked: number; // UNAVAILABLE staff
  pendingStaff: number; // Active staff who haven't submitted availability
  total: number; // Total schedules
  staffInfo: Array<{
    staffId: string;
    availability: StaffAvailability;
    note?: string;
    staffName?: string;
    isDriver?: boolean;
    isLead?: boolean;
    bookedHours?: number;
    availableHours?: number;
  }>;
  pendingStaffInfo: Array<{
    staffId: string;
    staffName?: string;
    isDriver?: boolean;
    isLead?: boolean;
  }>;
}

export const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  staffId,
}) => {
  // State for the current week (instead of month)
  const [currentWeek, setCurrentWeek] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [error, setError] = useState<string | null>(null);

  // State for tracking availability data
  const [availabilityMap, setAvailabilityMap] = useState<
    Record<string, StaffCounts>
  >({});

  // Calculate date range for data fetching (3 weeks)
  const dateRange = useMemo(() => {
    const weekStart = dayjs(currentWeek).startOf("week");
    const weekEnd = weekStart.add(20, "day").endOf("day"); // 3 weeks = 21 days (0-20)
    return {
      startDate: weekStart.startOf("day").toISOString(),
      endDate: weekEnd.toISOString(),
    };
  }, [currentWeek]);

  // Determine if this is a single staff view or team view
  const isSingleStaffView = !!staffId;

  // Fetch staff availability data
  const { data: scheduleData, isLoading, error: queryError } = useGetStaffAvailabilities(
    {
      staffId, // If staffId is defined, it will filter for that staff only
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      pageSize: 100, // Increased page size to get more data
    },
    true // Always enabled
  );

  // Fetch all staff for pending calculation (only for team view)
  // Note: When active/hiatus/inactive statuses are added, filter out inactive and hiatus
  const { data: allStaffData } = useGetAllStaff(
    {
      pageSize: 500, // Max allowed by backend validation
    },
    !isSingleStaffView // Only fetch for team view
  );

  // Process the fetched data into a map for easy lookup by date
  useEffect(() => {
    try {
      setError(null);

      if (!isLoading && scheduleData?.items) {
        const newAvailabilityMap: Record<string, StaffCounts> = {};

        // Group availabilities by date
        scheduleData.items.forEach((schedule: StaffAvailabilitySchedule) => {
          if (!schedule || !schedule.date) return;

          const dateKey = dayjs(schedule.date).format("YYYY-MM-DD");

          // Initialize the date entry if it doesn't exist
          if (!newAvailabilityMap[dateKey]) {
            newAvailabilityMap[dateKey] = {
              availableStaff: 0,
              availableDrivers: 0,
              availableLeads: 0,
              staffBooked: 0,
              pendingStaff: 0,
              total: 0,
              staffInfo: [],
              pendingStaffInfo: [],
            };
          }

          // Update counts based on availability and role
          // Only FULL_TIME counts as available; PART_TIME is treated as unavailable
          if (schedule.availability === StaffAvailability.FULL_TIME) {
            newAvailabilityMap[dateKey].availableStaff += 1;
            if (schedule.staff?.driver) {
              newAvailabilityMap[dateKey].availableDrivers += 1;
            }
            if (schedule.staff?.lead) {
              newAvailabilityMap[dateKey].availableLeads += 1;
            }
          } else if (schedule.availability === StaffAvailability.UNAVAILABLE ||
                     schedule.availability === StaffAvailability.PART_TIME) {
            newAvailabilityMap[dateKey].staffBooked += 1;
          }

          // Track total staff entries for this date
          newAvailabilityMap[dateKey].total += 1;

          // Store individual staff info for detailed view
          const staffName = formatStaffName(schedule.staff);

          newAvailabilityMap[dateKey].staffInfo.push({
            staffId: schedule.staffId,
            availability: schedule.availability || StaffAvailability.FULL_TIME,
            note: schedule.note || undefined,
            staffName,
            isDriver: schedule.staff?.driver || false,
            isLead: schedule.staff?.lead || false,
            bookedHours: schedule.bookedHours || 0,
            availableHours: schedule.availableHours || 8,
          });
        });

        // Calculate pending staff (active staff who haven't submitted availability)
        if (allStaffData?.items && !isSingleStaffView) {
          const weekStart = dayjs(currentWeek).startOf("week");
          const weekEnd = weekStart.add(20, "day"); // 3 weeks = 21 days (0-20)
          let currentDay = weekStart.clone();

          while (currentDay.isSameOrBefore(weekEnd)) {
            const dateKey = currentDay.format("YYYY-MM-DD");

            if (!newAvailabilityMap[dateKey]) {
              newAvailabilityMap[dateKey] = {
                availableStaff: 0,
                availableDrivers: 0,
                availableLeads: 0,
                staffBooked: 0,
                pendingStaff: 0,
                total: 0,
                staffInfo: [],
                pendingStaffInfo: [],
              };
            }

            // Get staff IDs who submitted for this date
            const submittedStaffIds = new Set(
              newAvailabilityMap[dateKey].staffInfo.map(s => s.staffId)
            );

            // Find active staff who haven't submitted (exclude HIATUS and INACTIVE)
            allStaffData.items.forEach((staff: any) => {
              const isActiveStaff = staff.status !== 'HIATUS' && staff.status !== 'INACTIVE';
              if (!submittedStaffIds.has(staff.id) && isActiveStaff) {
                newAvailabilityMap[dateKey].pendingStaff += 1;
                newAvailabilityMap[dateKey].pendingStaffInfo.push({
                  staffId: staff.id,
                  staffName: formatStaffName(staff),
                  isDriver: staff.driver || false,
                  isLead: staff.lead || false,
                });
              }
            });

            currentDay = currentDay.add(1, 'day');
          }
        }

        setAvailabilityMap(newAvailabilityMap);
      }
    } catch (err) {
      console.error("Error processing availability data:", err);
      setError("Failed to load availability data. Please try again.");
    }
  }, [isLoading, scheduleData, allStaffData, currentWeek, isSingleStaffView]);

  // Get the status and staff counts for a specific date
  const getDateStatus = (date: dayjs.Dayjs) => {
    const dateString = date.format("YYYY-MM-DD");
    const dateData = availabilityMap[dateString];

    if (!dateData) {
      return {
        availableStaff: 0,
        availableDrivers: 0,
        availableLeads: 0,
        staffBooked: 0,
        pendingStaff: 0,
        total: 0,
        staffInfo: [],
        pendingStaffInfo: [],
      };
    }

    return dateData;
  };

  // Generate the days to display in the calendar (3 weeks = 21 days)
  const startDate = dayjs(currentWeek).startOf("week");
  const endDate = startDate.add(20, "day"); // 3 weeks = 21 days (0-20)

  // Generate an array of days for 3 weeks
  const days = useMemo(() => {
    const daysArray = [];
    let currentDay = startDate.clone();
    while (currentDay.isSameOrBefore(endDate)) {
      daysArray.push(currentDay);
      currentDay = currentDay.add(1, "day");
    }
    return daysArray;
  }, [startDate, endDate]);

  // Navigation handlers (move by 3 weeks)
  const goToPreviousPeriod = () => {
    setCurrentWeek(currentWeek.subtract(3, "week"));
  };

  const goToNextPeriod = () => {
    setCurrentWeek(currentWeek.add(3, "week"));
  };

  // Handle date selection
  const handleDateClick = (day: dayjs.Dayjs) => {
    setSelectedDate(day);
  };

  // Get color class based on the availability counts
  const getDateColor = useCallback(
    (dateData: StaffCounts) => {
      const { availableStaff, total } = dateData;

      // For single staff view, use simple color mapping
      if (isSingleStaffView) {
        if (total === 0) return "";
        const availability = dateData.staffInfo[0]?.availability;

        if (availability === StaffAvailability.FULL_TIME) return "bg-green-100";
        if (availability === StaffAvailability.PART_TIME)
          return "bg-yellow-100";
        if (availability === StaffAvailability.UNAVAILABLE) return "bg-red-50";
        return "";
      }

      // For team view, use percentage-based coloring
      if (total === 0) return "";

      const availablePercentage = availableStaff / total;

      if (availablePercentage >= 0.7) return "bg-green-100";
      if (availablePercentage >= 0.3) return "bg-yellow-100";
      return "bg-red-50";
    },
    [isSingleStaffView]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (error || queryError) {
    return (
      <div className="flex flex-col justify-center items-center h-64 text-center p-4">
        <div className="text-red-500 mb-2">⚠️ Error Loading Calendar</div>
        <div className="text-gray-600">{error || "Failed to load availability data"}</div>
      </div>
    );
  }

  // Render the calendar cell content based on view mode
  const renderCellContent = (day: dayjs.Dayjs, dateData: StaffCounts) => {
    if (isSingleStaffView) {
      // For single staff view, show simple availability status
      const availability = dateData.staffInfo[0]?.availability;
      const note = dateData.staffInfo[0]?.note;
      const isDriver = dateData.staffInfo[0]?.isDriver;
      const isLead = dateData.staffInfo[0]?.isLead;

      return (
        <>
          <div>{day.format("D")}</div>
          {availability && (
            <div className="text-xs mt-1">
              {availability === StaffAvailability.FULL_TIME && (
                <span className="text-green-600">
                  Available{isDriver ? " (Driver)" : isLead ? " (Lead)" : ""}
                </span>
              )}
              {availability === StaffAvailability.PART_TIME && (
                <span className="text-amber-600">
                  Half-day{isDriver ? " (Driver)" : isLead ? " (Lead)" : ""}
                </span>
              )}
              {availability === StaffAvailability.UNAVAILABLE && (
                <span className="text-red-600">Booked</span>
              )}
            </div>
          )}
          {note && (
            <div className="text-xs italic truncate max-w-[50px]">
              <Tooltip title={note}>
                <span className="text-gray-500">{note}</span>
              </Tooltip>
            </div>
          )}
        </>
      );
    }

    // For team view, show just the day number (click for details in sidebar)
    return (
      <div>{day.format("D")}</div>
    );
  };

  // Get selected date data for sidebar
  const selectedDateData = selectedDate ? getDateStatus(selectedDate) : null;

  return (
    <div className="p-6 w-full bg-white rounded-lg">
      {/* Header */}
      <h2 className="text-2xl font-semibold text-center mb-6">
        {isSingleStaffView
          ? "Staff Availability"
          : "Team Availability Calendar"}
      </h2>

      {/* Grid Layout: Calendar (70%) + Sidebar (30%) */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left: Calendar */}
        <div className="col-span-8">
          {/* Period Navigation */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={goToPreviousPeriod}
              className="text-gray-600 hover:text-gray-800 px-4 py-2 rounded hover:bg-gray-100 text-lg font-semibold"
            >
              ←
            </button>
            <h3 className="text-xl font-medium">
              {startDate.format("MMM D")} - {endDate.format("MMM D, YYYY")}
            </h3>
            <button
              onClick={goToNextPeriod}
              className="text-gray-600 hover:text-gray-800 px-4 py-2 rounded hover:bg-gray-100 text-lg font-semibold"
            >
              →
            </button>
          </div>

          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-2 text-center text-gray-600 font-semibold mb-3 text-base">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div key={day} className="p-3">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              const dateData = getDateStatus(day);
              const color = getDateColor(dateData);
              const isSelected = selectedDate && day.isSame(selectedDate, "day");
              const isToday = day.isSame(dayjs(), "day");

              return (
                <div
                  key={index}
                  onClick={() => handleDateClick(day)}
                  className={cn(
                    "p-3 text-center rounded-md cursor-pointer border transition-colors duration-200 min-h-[100px] flex flex-col items-center justify-start text-gray-800",
                    color,
                    isSelected
                      ? "border-blue-500 border-2"
                      : "border-transparent hover:bg-gray-100",
                    isToday ? "ring-2 ring-blue-400" : ""
                  )}
                >
                  {renderCellContent(day, dateData)}
                </div>
              );
            })}
      </div>

          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-6 mt-8">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-100 rounded border border-green-200"></div>
              <span className="text-sm font-medium">
                {isSingleStaffView ? "Available" : "Mostly Available (70%+)"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-yellow-100 rounded border border-yellow-200"></div>
              <span className="text-sm font-medium">
                {isSingleStaffView ? "Part Time" : "Partially Available (30-70%)"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-red-50 rounded border border-red-200"></div>
              <span className="text-sm font-medium">
                {isSingleStaffView ? "Unavailable" : "Mostly Booked (<30%)"}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Staff Details Sidebar */}
        <div className="col-span-4">
          <StaffDetailsSidebar
            selectedDate={selectedDate}
            staffInfo={selectedDateData?.staffInfo || []}
            pendingStaffInfo={selectedDateData?.pendingStaffInfo || []}
            availableCount={selectedDateData?.availableStaff || 0}
            unavailableCount={selectedDateData?.staffBooked || 0}
            pendingCount={selectedDateData?.pendingStaff || 0}
          />
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
